/**
 * Full-screen, looping pixel-art carousel for choosing the game mode on TV.
 *
 * Portals into the TvCanvas content root (`container`) so it scales together
 * with the 900 px design canvas instead of escaping to <body>.
 */

import { useState, type CSSProperties } from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { useTranslation } from 'react-i18next'
import { EmojiText } from '@/components/low/AnimatedEmoji'
import { PixelIcon } from '@/components/low/PixelIcon'
import type { GameModeListItem } from '@/services/api'

interface GameModeCarouselProps {
  open: boolean
  modes: GameModeListItem[]
  selectedModeId: string
  playerCount: number
  container: HTMLElement
  onSelect: (modeId: string) => void
  onClose: () => void
}

/** Visible slots relative to the current mode: half-cut neighbours on both edges. */
const SLOT_OFFSETS = [-1, 0, 1] as const
type SlotOffset = (typeof SLOT_OFFSETS)[number]

const SLOT_POSITION: Record<SlotOffset, string> = {
  [-1]: 'left-0 -translate-x-1/2',
  0: 'left-1/2 -translate-x-1/2',
  1: 'left-full -translate-x-1/2',
}

const DITHER_TILE = '8px 8px'
const BG = 'hsl(var(--background))'

/** Edge bands from the outermost inwards: 75%, 50%, 25% coverage. */
const DITHER_BANDS: CSSProperties[] = [
  { background: `conic-gradient(at 4px 4px, ${BG} 75%, transparent 0) 0 0 / ${DITHER_TILE}` },
  { background: `repeating-conic-gradient(${BG} 0 25%, transparent 0 50%) 0 0 / ${DITHER_TILE}` },
  { background: `conic-gradient(at 4px 4px, transparent 75%, ${BG} 0) 0 0 / ${DITHER_TILE}` },
]

function isModeBlocked(mode: GameModeListItem, playerCount: number): boolean {
  return mode.max_players < playerCount
}

function wrapIndex(index: number, length: number): number {
  return ((index % length) + length) % length
}

function DitherEdge({ side }: { side: 'left' | 'right' }) {
  return (
    <div
      className={`absolute inset-y-0 ${side === 'left' ? 'left-0 flex-row' : 'right-0 flex-row-reverse'} flex w-[9%] pointer-events-none z-10`}
    >
      {DITHER_BANDS.map((style, i) => (
        <div key={i} className="h-full flex-1" style={style} />
      ))}
    </div>
  )
}

export function GameModeCarousel({
  open,
  modes,
  selectedModeId,
  playerCount,
  container,
  onSelect,
  onClose,
}: GameModeCarouselProps) {
  const { t } = useTranslation()
  const initialIndex = Math.max(0, modes.findIndex((m) => m.id === selectedModeId))
  const [index, setIndex] = useState(initialIndex)

  const handleOpenChange = (next: boolean) => {
    if (!next) onClose()
  }

  const step = (delta: number) => setIndex((current) => wrapIndex(current + delta, modes.length))

  const current = modes[wrapIndex(index, modes.length)]
  const currentBlocked = isModeBlocked(current, playerCount)

  return (
    <DialogPrimitive.Root open={open} onOpenChange={handleOpenChange}>
      <DialogPrimitive.Portal container={container}>
        <DialogPrimitive.Overlay className="absolute inset-0 z-50 bg-background" />
        <DialogPrimitive.Content
          className="absolute inset-0 z-50 flex flex-col overflow-hidden bg-background animate-fade-in focus:outline-none"
        >
          <DialogPrimitive.Title className="font-display text-5xl text-center text-foreground uppercase tracking-wider pt-12">
            {t('lobby.chooseMode')}
          </DialogPrimitive.Title>
          <DialogPrimitive.Description className="sr-only">
            {t('lobby.chooseModeDescription')}
          </DialogPrimitive.Description>

          <div className="relative flex-1 min-h-0">
            {SLOT_OFFSETS.map((offset) => {
              const mode = modes[wrapIndex(index + offset, modes.length)]
              const isBlocked = isModeBlocked(mode, playerCount)
              const needsMore = mode.min_players > playerCount
              const className = `absolute top-1/2 -translate-y-1/2 ${SLOT_POSITION[offset]} w-[36%] h-[78%] animate-fade-in pixel-frame-lg bg-card flex flex-col items-center justify-center gap-6 p-8 ${
                isBlocked ? 'grayscale opacity-60' : ''
              }`
              const body = (
                <>
                  <span className="text-[160px] leading-none">
                    <EmojiText>{mode.icon}</EmojiText>
                  </span>
                  <span className="font-display text-4xl text-center text-foreground uppercase tracking-wide">
                    {mode.title}
                  </span>
                  <span className="text-lg text-muted-foreground">
                    {t('lobby.playerCount', { count: mode.players })}
                  </span>
                  {isBlocked && (
                    <span className="text-base font-bold text-destructive text-center">
                      {t('lobby.modeTooManyPlayers', { count: mode.max_players })}
                    </span>
                  )}
                  {!isBlocked && needsMore && (
                    <span className="text-base font-bold text-warning text-center">
                      {t('lobby.modeNeedsMorePlayers', { count: mode.min_players })}
                    </span>
                  )}
                </>
              )
              // Keys include the index: remounting on every move replays the stepped fade-in.
              return offset === 0 ? (
                <div key={`${offset}-${index}`} className={className}>
                  {body}
                </div>
              ) : (
                <button
                  key={`${offset}-${index}`}
                  type="button"
                  aria-label={mode.title}
                  onClick={() => step(offset)}
                  className={`${className} pixel-press`}
                >
                  {body}
                </button>
              )
            })}

            <DitherEdge side="left" />
            <DitherEdge side="right" />
          </div>

          <div className="flex justify-center items-center gap-8 pb-12 pt-6">
            <button
              type="button"
              onClick={() => step(-1)}
              className="pixel-frame pixel-press bg-primary text-primary-foreground w-16 h-16 flex items-center justify-center text-3xl"
            >
              <PixelIcon label={t('lobby.previousMode')}>◀</PixelIcon>
            </button>
            <button
              type="button"
              disabled={currentBlocked}
              onClick={() => onSelect(current.id)}
              className="pixel-frame pixel-press bg-primary text-primary-foreground px-12 py-4 text-2xl font-black uppercase tracking-widest disabled:opacity-50"
            >
              {t('lobby.selectMode')}
            </button>
            <button
              type="button"
              onClick={() => step(1)}
              className="pixel-frame pixel-press bg-primary text-primary-foreground w-16 h-16 flex items-center justify-center text-3xl"
            >
              <PixelIcon label={t('lobby.nextMode')}>▶</PixelIcon>
            </button>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}
